/**
 * ORCHID INSIGHTS — the numbers, and the file they leave in.
 *
 * Script 161 is where the arithmetic lives. This file fetches it and turns the
 * export into a CSV, and does no counting of its own: a dashboard whose totals
 * are computed in the browser is a dashboard whose totals disagree with the
 * export the moment either changes.
 *
 * WHY THE CHART DOES NOT ASK FOR A REASON
 *
 * Every subject-scoped read in the portal demands one, and this does not. That
 * is deliberate and it is script 161's reasoning, repeated here because this is
 * the file somebody would "fix" it in: a reason field binds a named person's
 * record to a named purpose, and there is no named person in an aggregate. A
 * gate on the dashboard would be typed through forty times a week, and a reason
 * people fill in reflexively has stopped being evidence anywhere — including on
 * the reads where it matters. The look is still logged, with who and when.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const fail = (error) => ({ data: null, error });

const call = async (fn, args = {}) => {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc(fn, args);
  if (error) {
    const raw = error.message || '';
    if (/do not hold view_platform_analytics/i.test(raw)) {
      return fail('Your account does not hold view_platform_analytics.');
    }
    if (/do not hold export_aggregate_analytics/i.test(raw)) {
      return fail('Your account does not hold export_aggregate_analytics, so this file cannot be built.');
    }
    return fail(raw || 'Insights could not be read.');
  }
  return { data: data || [], error: null };
};

/** What can be asked for. Read from the database so a new metric appears here. */
export const fetchMetricCatalogue = () => call('fn_orchid_insight_metrics');

/** The headline tiles, this month against last. */
export const fetchHeadline = () => call('fn_orchid_insights', { p_reason: null });

/** One metric, month by month, oldest first — the order a chart draws in. */
export const fetchSeries = (metricKey, months = 12) =>
  call('fn_orchid_insights_series', { p_metric: metricKey, p_months: months, p_reason: null });

/** One dimension, sliced and suppressed. */
export const fetchBreakdown = (dimension) =>
  call('fn_orchid_insights_breakdown', { p_dimension: dimension, p_reason: null });

/** Everything flat, one row per fact. */
export const fetchExport = (months = 12) =>
  call('fn_export_orchid_insights', { p_months: months, p_reason: null });

/**
 * The dimensions the database will slice by, in the order they read best.
 *
 * THE LAST THREE ARE SCRIPT 335's, and the founder named them: "payment
 * consistency to the day; property maintenance by suburb, units held per
 * landlord by city." They lead because they are what a bank, an insurer or a
 * development partner opens this page to see, and the trade and tier slices are
 * Orchid's own housekeeping by comparison.
 *
 * SUBURB IS `township`, WHICH IS A MAPPING AND NOT A RENAME. There is no suburb
 * column: `properties` carries city and township, and township is the
 * suburb-level locality here because an erf number is unique only within one.
 * That is the deeds convention this schema was built on.
 */
export const DIMENSIONS = [
  { key: 'payment_punctuality',     label: 'By payment consistency' },
  { key: 'maintenance_suburb',      label: 'By suburb' },
  { key: 'units_per_landlord_city', label: 'By city' },
  { key: 'trade',                   label: 'By trade' },
  { key: 'province',                label: 'By province' },
  { key: 'tier',                    label: 'By tier' },
  { key: 'role',                    label: 'By role' },
  { key: 'job_status',              label: 'By job status' },
  { key: 'provider_band',           label: 'By reputation band' },
  { key: 'application_outcome',     label: 'By application outcome' },
];

/**
 * Turn the flat export into a CSV.
 *
 * ONE FIELD IS DELIBERATELY NOT BLANK. A suppressed cell exports with an empty
 * value and the word "suppressed" in its own column, rather than a zero. A zero
 * would be a lie a spreadsheet would happily add up, and the person opening the
 * file has no way to tell an absence from a nothing unless the file says which.
 */
export function toCsv(rows) {
  const header = [
    'kind', 'family', 'metric_key', 'metric', 'period_start', 'period_label',
    'bucket', 'unit', 'value', 'subjects', 'suppressed',
  ];

  // RFC 4180: quote everything that could carry a comma, a quote or a newline,
  // and double an embedded quote. Trade names contain commas often enough that
  // skipping this shifts every column right on one row in a hundred.
  const cell = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const lines = [header.join(',')];
  (rows || []).forEach((r) => {
    lines.push(header.map((h) => cell(r[h])).join(','));
  });
  // A trailing newline: some tools drop the last row without one.
  return `${lines.join('\r\n')}\r\n`;
}

/** The filename a person will later have to recognise in a downloads folder. */
export const csvFilename = (months) => {
  const stamp = new Date().toISOString().slice(0, 10);
  return `orchid-insights-${months}-months-${stamp}.csv`;
};

/** How a movement reads in a sentence. Null means there was nothing before. */
export const movementLabel = (movement) => {
  if (movement === null || movement === undefined) return 'nothing last month';
  const n = Number(movement);
  if (Number.isNaN(n)) return 'no comparison';
  if (n === 0) return 'level with last month';
  return `${n > 0 ? '+' : ''}${n}% on last month`;
};

/** Whether a movement should read as good, bad or neither. */
export const movementTone = (metricKey, movement) => {
  if (movement === null || movement === undefined || Number(movement) === 0) return 'quiet';
  // MORE IS NOT ALWAYS BETTER. Enquiries rising is a support load, not growth,
  // and colouring it green would make the dashboard congratulate Orchid for a
  // problem. These are the metrics where up is down.
  const inverted = new Set(['tickets_raised']);
  const up = Number(movement) > 0;
  const good = inverted.has(metricKey) ? !up : up;
  return good ? 'green' : 'warning';
};

/** R1 234,56 the way South Africa writes it; plain counts stay plain. */
export const formatValue = (value, unit) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  if (unit === 'rand') {
    return `R${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return n.toLocaleString('en-ZA');
};

/**
 * WHAT PEOPLE ASKED FOR AND ORCHID COULD NOT NAME A TRADE FOR.
 *
 *   "people who typed services they need but that weren't yet provided by the
 *    platform, we could get a running tally of those requested services, and
 *    then decide based on demand whether or not we want to add those extra
 *    services and their providers to the platform."   -- the founder
 *
 * Script 255 does the counting and the suppressing. This does neither, for the
 * same reason nothing else in this file does: a total computed in the browser
 * is a total that disagrees with the export the moment either changes.
 *
 * The floor is already applied on the way out -- fn_insights_min_cell(), the
 * same five every other cell in this dashboard obeys -- so a phrase fewer than
 * five separate askers used never arrives here at all. There is nothing for
 * this file to hide, and it must not try: filtering again in the browser would
 * be a second floor that agrees today and drifts later.
 */
export const fetchUnmetDemand = (province = null, limit = 50) =>
  call('fn_unmet_service_demand', { p_province: province, p_limit: limit });
